import {
    Component,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    Input,
    SimpleChanges,
    HostBinding,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { navigationButtons } from 'layout/navigation-buttons';
import { AccessCondition, AccessString, AuthService, PermissionsHelper } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { FeatureFlagService } from '../../services/feature-flags';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { BetaLinkService } from '../layout-service/beta-link.service';
import { BetaNotificationDialog } from '../beta-notification-dialog/beta-notification-dialog.component';

@Component({
    selector: 'ce-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
@UntilDestroy()
export class TopbarComponent {
    @Input() pageId: string;
    @Output() menuClicked = new EventEmitter<void>();
    @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    filteredButtons: Partial<typeof navigationButtons> = [];
    organizationName$ = new BehaviorSubject(null);
    licenseName$ = new BehaviorSubject(null);
    isSearchFieldActive$ = new BehaviorSubject(false);
    hasPermissionsToSearch$ = new BehaviorSubject(false);

    featureFlagState: { [key: string]: boolean } = {};
    isDarkMode = false;

    sectionName: string;

    chargeye2Link$ = this.betaLinkService.getBetaLinkState$();

    constructor(
        private auth: AuthService,
        private featureFlagService: FeatureFlagService,
        private cdRef: ChangeDetectorRef,
        private orgService: OrganizationInfoService,
        private licenseService: LicenseInfoService,
        private readonly environmentService: EnvironmentService,
        private readonly router: Router,
        private betaLinkService: BetaLinkService
    ) {
        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = origin === 'mykempower' ? 'theme-mykempower' : 'theme-chargeye';
                this.cdRef.markForCheck();
            });

        // Subscribe separately to organization and license info to ensure they update immediately
        this.orgService.organizationInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((organizationInfo) => {
                const orgName =
                    organizationInfo?.impersonated?.tenantName || organizationInfo?.originalTenantName || '';
                this.organizationName$.next(orgName);
                this.cdRef.markForCheck();
            });

        this.licenseService.licenseInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((license) => {
                const licName = license?.licenseDefinition?.name || license?.licenseDefinition?.type || '';
                this.licenseName$.next(licName);
                this.cdRef.markForCheck();
            });

        combineLatest([
            this.auth.authenticated$,
            this.featureFlagService.getFlagState$(),
            this.auth.permissions$,
            this.orgService.organizationInfo$,
            this.licenseService.licenseInfo$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([, flagState, helper, _organizationInfo, _license]) => {
                this.featureFlagState = flagState;
                this.isDarkMode = flagState['enableDarkMode'] || false;

                this.refreshButtons(helper);
                this.cdRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.pageId) {
            if (this.pageId === 'iam') {
                this.sectionName = 'Account administration';
            } else {
                this.sectionName = '';
            }
            this.cdRef.markForCheck();
        }
    }

    navigateToHome(): void {
        if (this.themeClass === 'theme-mykempower') {
            // For MyKempower, always go to stations
            this.router.navigate(['/search/stations']);
        } else {
            // For ChargEye, go to root (which will use default routing)
            this.router.navigate(['/']);
        }
    }

    setSearchActive($event): void {
        this.isSearchFieldActive$.next($event);
    }

    navigateToBeta(): void {
        this.betaLinkService.navigateToBeta(false, BetaNotificationDialog);
    }

    private refreshButtons(helper: PermissionsHelper): void {
        this.filteredButtons = [];

        this.hasPermissionsToSearch$.next(helper?.hasAccess('fleet.chargingStation.overview.globalSearch'));

        for (const b of navigationButtons) {
            // check duplicate links
            const linkExisted = this.filteredButtons.some((_b) => _b.routerLink === b.routerLink);
            if (linkExisted) {
                continue;
            }

            // tenant id filter
            if (b.tenantIds && this.auth.profile?.tenantId && !b.tenantIds.includes(this.auth.profile.tenantId)) {
                // don't add this button, for a different tenant id
            }

            // check access
            if (b.access) {
                const condition: AccessCondition = b.accessCondition || 'AND';
                const accessString: AccessString = b.access;

                if (!helper?.hasAccess(accessString, condition)) {
                    continue;
                }
            }

            // feature flag filter
            if (b.featureFlag && !this.featureFlagState[b.featureFlag]) {
                continue;
            }

            // sys admin filter - override access filter
            if (b.sysAdminOnly && !this.auth.getPermissions().isSystemAdmin) {
                continue;
            }

            this.filteredButtons.push(b);
        }

        this.cdRef.markForCheck();
    }
}
